import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { SocialWebStoryScopeWidget } from 'schemas/SocialWebStoryScopeWidgetSchema'
// @ts-ignore
import { Tooltip, Dialog } from '@netbasequid/core'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import {
  TopInfoBox,
  TopInfoLeft,
  TopInfoRight,
  StyledTermContainer,
  StyledTermContent
} from 'components/insightContent/visualization/InfoBox/index.css'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { CountTypeStrings } from 'enums/CountType'
import AccordionGroup from 'components/insightContent/visualization/AccordionGroup'
import { getInsightContentTypeDisplayText } from 'components/insightContent/utils/utils'
import AccordionSectionType from 'components/insightContent/enum/AccordionSectionType'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { InsightContentSettingType } from 'components/insightContent/types'
import { useEmojiNameToDisplayMap } from 'hooks/useEmojiState'
import messageUtil from 'utils/messageUtil'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'
import { generateSelectedTerms } from './utils'

const Name = styled.span`
  font-weight: ${sys.fontWeight.semibold};
`

const DIALOG_CONTENT_HEIGHT = '460px'

interface InsightModalProps {
  widgetId: number
  open: boolean
  onClose: () => void
  title?: string
  selectedInsights?: InsightData[]
  widgetSettingWithSelection?: any
  timeUnits?: MetricTimeUnitStrings[]
  forceNotScale?: boolean
  countType?: CountTypeStrings
  detailSetting?: InsightContentSettingType
}

const InsightModal = ({
  widgetId,
  open,
  title,
  selectedInsights,
  onClose,
  widgetSettingWithSelection,
  timeUnits,
  forceNotScale,
  countType,
  detailSetting
}: InsightModalProps) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()
  const widgetSetting = useDashboardWidgetSetting<SocialWebStoryScopeWidget>(widgetId)
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()
  const selectedTerms = useMemo(() => {
    if (selectedInsights && selectedInsights.length > 0) {
      return generateSelectedTerms(selectedInsights[0], widgetSetting, emojiNameToDisplayMap)
    }
    return []
  }, [selectedInsights, widgetSetting, emojiNameToDisplayMap])

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSettingWithSelection))
  }

  return (
    <InsightDialog open={open} onClose={onClose}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <TopInfoBox mode={theme}>
        <TopInfoLeft>
          {selectedTerms?.map(({ name, tooltip }, index) => (
            <React.Fragment key={name}>
              <StyledTermContainer>
                {/* eslint-disable-next-line no-unneeded-ternary */}
                <Tooltip mode={theme} enabled={tooltip ? true : false} content={tooltip}>
                  <StyledTermContent
                    mode={theme}
                    testId={
                      selectedTerms.length > 1
                        ? `${AutomationId.insightModal.selectedTerm}-${index}`
                        : undefined
                    }>
                    {name}
                  </StyledTermContent>
                </Tooltip>
              </StyledTermContainer>
              {index < selectedTerms.length - 1 && (
                <span {...{ [AUTO_ATTR_KEY]: AutomationId.insightModal.and }}>
                  &nbsp;{t('and')}&nbsp;
                </span>
              )}
            </React.Fragment>
          ))}
        </TopInfoLeft>
        <TopInfoRight>
          <GoToAnalysisButton onClick={() => goToAnalysis()} />
        </TopInfoRight>
      </TopInfoBox>
      <Dialog.DialogContent height={DIALOG_CONTENT_HEIGHT}>
        <AccordionGroup
          timeUnits={timeUnits}
          forceNotScale={forceNotScale}
          countType={countType}
          insightContentsConfig={[
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SUMMARY),
              accordionContents: [
                {
                  insightContentType: InsightContentType.TOTAL_MEASURE,
                  detailSetting
                },
                {
                  insightContentType: InsightContentType.NAME_MEASURE,
                  detailSetting,
                  renderNameMeasureTitleContent: detailSetting?.selectionSecondName
                    ? () => (
                        <div>
                          <Trans
                            i18nKey="of"
                            values={{
                              selectionName: detailSetting?.selectionName,
                              selectionSecondName: detailSetting?.selectionSecondName,
                              countType: messageUtil.countType(countType!)
                            }}>
                            <Name />
                          </Trans>
                        </div>
                      )
                    : undefined
                }
              ]
            },
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SOUND_BITE),
              accordionContents: [
                {
                  insightContentType: InsightContentType.SOUND_BITE,
                  detailSetting
                }
              ]
            }
          ]}
        />
      </Dialog.DialogContent>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
